import * as Keychain from 'react-native-keychain';
import AsyncStorage from '@react-native-async-storage/async-storage';
import CryptoJS from 'crypto-js';
import { Platform } from 'react-native';

const PIN_KEYCHAIN_SERVICE = 'one-app-pin';
const PIN_SETUP_COMPLETED_SERVICE = 'one-app-pin-setup-completed';
// Failed attempts and the lockout deadline. Kept in the Keychain beside the
// PIN itself: held in memory only, they were reset by killing the app, so
// the PIN could be guessed four tries at a time without ever being locked.
const PIN_ATTEMPTS_SERVICE = 'one-app-pin-attempts';
const PIN_ENABLED_KEY = '@pin_enabled';
const PIN_SKIPPED_KEY = '@pin_skipped';

const PBKDF2_ITERATIONS = 1_0;
const PBKDF2_KEY_SIZE = 256 / 32; // 256 bits

/**
 * Generate a 16-byte hex salt.
 * Uses Math.random() — sufficient for uniqueness as a salt.
 * Security comes from Keychain isolation + failed-attempt lockout,
 * not from salt entropy.
 */
const generateSalt = (): string => {
    const bytes = new Uint8Array(16);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(bytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
};

/**
 * Hash a PIN using PBKDF2-HMAC-SHA256 with the given salt
 */
/**
 * Web: the hash sits in localStorage, readable by anyone with the browser profile, so it gets a real work
 * factor (OWASP's 600 000 for PBKDF2-SHA256). WebCrypto does that in a fraction of a second; CryptoJS would
 * take minutes. A 4-digit PIN is still only 10 000 guesses — this slows an offline attack, it can't stop one.
 */
const WEB_PBKDF2_ITERATIONS = 600_000;

const hashPINWeb = async (pin: string, salt: string): Promise<string> => {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveBits']);
    const bits = await crypto.subtle.deriveBits(
        { name: 'PBKDF2', hash: 'SHA-256', salt: enc.encode(salt), iterations: WEB_PBKDF2_ITERATIONS },
        key,
        256,
    );
    return Array.from(new Uint8Array(bits))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
};

const hashPIN = async (pin: string, salt: string): Promise<string> => {
    if (Platform.OS === 'web') {
        return hashPINWeb(pin, salt);
    }
    return CryptoJS.PBKDF2(pin, salt, {
        keySize: PBKDF2_KEY_SIZE,
        iterations: PBKDF2_ITERATIONS,
        hasher: CryptoJS.algo.SHA256,
    }).toString(CryptoJS.enc.Hex);
};

/**
 * Save PIN securely to Keychain
 */
export const savePIN = async (pin: string): Promise<boolean> => {
    try {
        const salt = generateSalt();
        const hashedPIN = await hashPIN(pin, salt);

        // Store salt as username and hash as password
        await Keychain.setGenericPassword(salt, hashedPIN, {
            service: PIN_KEYCHAIN_SERVICE,
        });

        // Mark PIN as enabled
        await AsyncStorage.setItem(PIN_ENABLED_KEY, 'true');
        // Clear skipped flag if it was set
        await AsyncStorage.removeItem(PIN_SKIPPED_KEY);
        // Mark PIN setup as completed (persists in Keychain)
        await markPINSetupCompleted();

        console.log('PIN saved successfully');
        return true;
    } catch (error) {
        console.error('Error saving PIN:', error);
        return false;
    }
};

/**
 * Verify PIN against stored hash
 */
export const verifyPIN = async (pin: string): Promise<boolean> => {
    try {
        const credentials = await Keychain.getGenericPassword({
            service: PIN_KEYCHAIN_SERVICE,
        });

        if (!credentials) {
            console.log('No PIN stored');
            return false;
        }

        // username = salt, password = PBKDF2 hash
        const { username: salt, password: storedHash } = credentials;
        const hashedInput = await hashPIN(pin, salt);
        const isValid = storedHash === hashedInput;

        return isValid;
    } catch (error) {
        console.error('Error verifying PIN:', error);
        return false;
    }
};

/**
 * Check if PIN has been set up
 */
export const hasPIN = async (): Promise<boolean> => {
    try {
        const enabled = await AsyncStorage.getItem(PIN_ENABLED_KEY);
        if (enabled !== 'true') {
            return false;
        }

        const credentials = await Keychain.getGenericPassword({
            service: PIN_KEYCHAIN_SERVICE,
        });

        return credentials !== false;
    } catch (error) {
        console.error('Error checking PIN:', error);
        return false;
    }
};

/**
 * Check if user has skipped PIN setup
 */
export const hasPINSkipped = async (): Promise<boolean> => {
    try {
        const skipped = await AsyncStorage.getItem(PIN_SKIPPED_KEY);
        return skipped === 'true';
    } catch (error) {
        console.error('Error checking PIN skipped:', error);
        return false;
    }
};

/**
 * Mark PIN setup as skipped
 */
export const skipPINSetup = async (): Promise<boolean> => {
    try {
        await AsyncStorage.setItem(PIN_SKIPPED_KEY, 'true');
        // Mark PIN setup as completed (persists in Keychain)
        await markPINSetupCompleted();
        console.log('PIN setup skipped');
        return true;
    } catch (error) {
        console.error('Error skipping PIN setup:', error);
        return false;
    }
};

/**
 * Clear PIN and reset all PIN-related settings
 */
export const clearPIN = async (): Promise<boolean> => {
    try {
        await Keychain.resetGenericPassword({ service: PIN_KEYCHAIN_SERVICE });
        await Keychain.resetGenericPassword({ service: PIN_SETUP_COMPLETED_SERVICE });
        await Keychain.resetGenericPassword({ service: PIN_ATTEMPTS_SERVICE });
        await AsyncStorage.removeItem(PIN_ENABLED_KEY);
        await AsyncStorage.removeItem(PIN_SKIPPED_KEY);
        console.log('PIN cleared');
        return true;
    } catch (error) {
        console.error('Error clearing PIN:', error);
        return false;
    }
};

/**
 * Change PIN (verify old PIN first, then save new one)
 */
export const changePIN = async (oldPIN: string, newPIN: string): Promise<boolean> => {
    try {
        const isValid = await verifyPIN(oldPIN);
        if (!isValid) {
            console.log('Old PIN is incorrect');
            return false;
        }

        return await savePIN(newPIN);
    } catch (error) {
        console.error('Error changing PIN:', error);
        return false;
    }
};

/**
 * Get lockout duration based on failed attempts
 * 1-5: No lockout
 * 6-15: 30 seconds
 * 16+: 1 minute
 */
export const getLockoutDuration = (failedAttempts: number): number => {
    if (failedAttempts < 5) {
        return 0;
    } else if (failedAttempts <= 15) {
        return 30 * 1000; // 30 seconds in ms
    } else {
        return 60 * 1000; // 1 minute in ms
    }
};

/**
 * Check if currently locked out
 */
export const isLockedOut = (lockoutUntil: Date | null): boolean => {
    if (!lockoutUntil) {
        return false;
    }
    return new Date() < lockoutUntil;
};

/**
 * Get remaining lockout time in seconds
 */
export const getRemainingLockoutTime = (lockoutUntil: Date | null): number => {
    if (!lockoutUntil) {
        return 0;
    }
    const remaining = lockoutUntil.getTime() - new Date().getTime();
    return Math.max(0, Math.ceil(remaining / 1000));
};

/**
 * Check if user has already completed PIN setup (set or skipped)
 * Uses Keychain so it persists even after app uninstall
 */
export const hasPINSetupCompleted = async (): Promise<boolean> => {
    try {
        const result = await Keychain.getGenericPassword({
            service: PIN_SETUP_COMPLETED_SERVICE,
        });
        return result !== false;
    } catch (error) {
        console.error('Error checking PIN setup completed flag:', error);
        return false;
    }
};

/**
 * Mark PIN setup as completed (called after user sets PIN or skips)
 * Uses Keychain so it persists even after app uninstall
 */
export const markPINSetupCompleted = async (): Promise<void> => {
    try {
        await Keychain.setGenericPassword('completed', 'true', {
            service: PIN_SETUP_COMPLETED_SERVICE,
        });
        console.log('PIN setup marked as completed');
    } catch (error) {
        console.error('Error marking PIN setup completed:', error);
    }
};


export interface PINAttemptsState {
    failedAttempts: number;
    lockoutUntil: Date | null;
}

/** The failed attempts and lockout left by the last run; none → zero and unlocked. */
export const loadPINAttempts = async (): Promise<PINAttemptsState> => {
    try {
        const stored = await Keychain.getGenericPassword({ service: PIN_ATTEMPTS_SERVICE });
        if (!stored) {
            return { failedAttempts: 0, lockoutUntil: null };
        }
        let parsed: { failedAttempts?: unknown; lockoutUntil?: unknown };
        try {
            parsed = JSON.parse(stored.password);
        } catch {
            return { failedAttempts: 0, lockoutUntil: null };
        }
        const failedAttempts = typeof parsed.failedAttempts === 'number' && parsed.failedAttempts > 0
            ? Math.floor(parsed.failedAttempts)
            : 0;
        const until = typeof parsed.lockoutUntil === 'string' ? new Date(parsed.lockoutUntil) : null;
        return {
            failedAttempts,
            lockoutUntil: until && !Number.isNaN(until.getTime()) ? until : null,
        };
    } catch (error) {
        console.error('Error loading PIN attempts:', error);
        return { failedAttempts: 0, lockoutUntil: null };
    }
};

/** Stores the attempts and lockout so a restart keeps them; zero attempts clears the entry. */
export const savePINAttempts = async (state: PINAttemptsState): Promise<void> => {
    try {
        if (state.failedAttempts <= 0) {
            await Keychain.resetGenericPassword({ service: PIN_ATTEMPTS_SERVICE });
            return;
        }
        await Keychain.setGenericPassword(
            'attempts',
            JSON.stringify({
                failedAttempts: state.failedAttempts,
                lockoutUntil: state.lockoutUntil ? state.lockoutUntil.toISOString() : null,
            }),
            { service: PIN_ATTEMPTS_SERVICE },
        );
    } catch (error) {
        console.error('Error saving PIN attempts:', error);
    }
};
